import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { http } from '../../../api/client';
import type { ClientOnboardingAlert } from '../../../api/clientOnboardingAlerts';

type Session = { userToken?: string; userRole?: string; activeRole?: string };
export type ClientChecklistState = { status?: ClientOnboardingAlert | null; loading: boolean; error: boolean };

/** One batch per visible page; a missing/failed read is never a completed checklist. */
export function useClientChecklists(eventIds: readonly string[], enabled = true) {
  const auth = useSelector((state: { auth: Session }) => state.auth);
  const role = String(auth.activeRole || auth.userRole || '').toLowerCase();
  const allowed = enabled && ['superadmin', 'admin', 'coordinator'].includes(role) && !!auth.userToken;
  const idsKey = [...new Set(eventIds.filter(id => /^[a-f0-9]{24}$/i.test(id)))].sort().join(',');
  const key = `${auth.userToken}|${role}|${allowed}|${idsKey}`;
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState<{ key: string; data: Record<string, ClientOnboardingAlert | null>; error: boolean }>();
  useEffect(() => {
    let current = true;
    if (!allowed || !idsKey) return;
    const ids = idsKey.split(',');
    setResult(undefined);
    const load = async () => {
      if (ids.length > 1000) throw new Error('Too many shows');
      const data: Record<string, ClientOnboardingAlert | null> = {};
      // Bounded chunks keep the request URL within proxy limits.
      for (let offset = 0; offset < ids.length; offset += 100) {
        if (!current) return;
        const requested = ids.slice(offset, offset + 100);
        const { response, error } = await http({ method: 'GET', url: 'dashboard/client-onboarding/checklist', params: { eventIds: requested.join(',') }, isToken: true });
        if (error) throw error;
        const rows = response?.data?.data?.shows;
        if (!Array.isArray(rows) || rows.length !== requested.length) throw new Error('Incomplete checklist response');
        for (const row of rows) {
          if (!requested.includes(row.eventId) || row.eventId in data) throw new Error('Invalid checklist response');
          data[row.eventId] = row.clientOnboarding ?? null;
        }
      }
      if (current) setResult({ key, data, error: false });
    };
    void load().catch(() => { if (current) setResult({ key, data: {}, error: true }); });
    return () => { current = false; };
  }, [key, idsKey, allowed, refresh]);
  return useMemo(() => {
    const data = result?.key === key && allowed ? result.data : {};
    return ({
    get: (eventId: string): ClientChecklistState => ({ status: data[eventId], loading: allowed && !!idsKey && result?.key !== key, error: !allowed || (result?.key === key && result.error) }),
    refresh: () => setRefresh(value => value + 1),
    allowed,
  });
  }, [allowed, idsKey, result, key]);
}
